import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  useCustomerGroupsQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
} from "@queries/customers";

const LANG_OPTS = [
  { v: "1", label: "English" },
  { v: "2", label: "Arabic" },
];
const YESNO_NUM = [{ v: "1", label: "Yes" }, { v: "0", label: "No" }];
const ENABLED_DISABLED = [{ v: "1", label: "Enabled" }, { v: "0", label: "Disabled" }];
const YESNO_ENUM = [{ v: "yes", label: "Yes" }, { v: "no", label: "No" }];
const PAY_METHODS = [
  { v: "free_checkout", label: "free checkout" },
  { v: "cc", label: "credit card" },
  { v: "cod", label: "cash on delivery " },
  { v: "ccod", label: "credit card on delivery " },
  { v: "cliq", label: "CliQ" },
];

export default function GeneralTab({ customer }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isEdit = !!customer?.customer_id;

  const { data: groupsResp, isLoading: groupsLoading } = useCustomerGroupsQuery();
  const groups = Array.isArray(groupsResp?.data) ? groupsResp.data : (groupsResp || []);

  const findIdByName = (name) => {
    const m = groups.find(g => (g.name ?? g.customer_group) === name);
    return m ? String(m.id ?? m.customer_group_id) : "";
  };

  const initGroup =
    customer?.customer_group_id != null
      ? String(customer.customer_group_id)
      : findIdByName(customer?.customer_group || "Default") || "";

  const [form, setForm] = React.useState({
    store_id: String(customer?.store_id ?? "3"),
    customer_group_id: initGroup,
    name: customer?.name || "",
    email: customer?.email || "",
    telephone: customer?.telephone || "",
    alt_mobile: customer?.alt_mobile || "",
    password: "",
    language_id: (customer?.language_id && [1, 2].includes(Number(customer.language_id))) ? String(customer.language_id) : "1",
    newsletter: customer?.newsletter != null ? String(Number(customer.newsletter)) : "0",
    customer_is_tax_free: customer?.customer_is_tax_free != null
      ? String(Number(customer.customer_is_tax_free)) : "0",
    discount_percentage: customer?.discount_percentage ?? "",
    status: customer?.status != null ? String(Number(customer.status)) : "1",
    safe: customer?.safe != null ? String(Number(customer.safe)) : "0",
    call_customer: customer?.call_customer === "yes" ? "yes" : "no",
    vip: customer?.vip === "yes" ? "yes" : "no",
    default_payment_method: customer?.default_payment_method ?? "cod",
  });

  React.useEffect(() => {
    setForm(prev => ({
      ...prev,
      store_id: String(customer?.store_id ?? prev.store_id ?? "3"),
      customer_group_id: initGroup || prev.customer_group_id,
      name: customer?.name ?? prev.name,
      email: customer?.email ?? prev.email,
      telephone: customer?.telephone ?? prev.telephone,
      alt_mobile: customer?.alt_mobile ?? prev.alt_mobile,
      language_id: (customer?.language_id && [1, 2].includes(Number(customer.language_id))) ? String(customer.language_id) : (prev.language_id || "1"),
      newsletter: customer?.newsletter != null ? String(Number(customer.newsletter)) : prev.newsletter,
      customer_is_tax_free: customer?.customer_is_tax_free != null
        ? String(Number(customer.customer_is_tax_free)) : prev.customer_is_tax_free,
      discount_percentage: customer?.discount_percentage ?? prev.discount_percentage,
      status: customer?.status != null ? String(Number(customer.status)) : prev.status,
      safe: customer?.safe != null ? String(Number(customer.safe)) : prev.safe,
      call_customer: customer?.call_customer === "yes" ? "yes" : prev.call_customer,
      vip: customer?.vip === "yes" ? "yes" : prev.vip,
      default_payment_method: customer?.default_payment_method ?? prev.default_payment_method,
    }));
  }, [customer?.customer_id, customer?.customer_group_id, customer?.customer_group, groups]);

  const [dirty, setDirty] = React.useState(false);
  const [msg, setMsg] = React.useState("");

  const createMut = useCreateCustomerMutation();
  const updateMut = useUpdateCustomerMutation();
  const saving = createMut.isPending || updateMut.isPending;

  const onChange = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setDirty(true);
    setMsg("");
  };

  const payload = {
    store_id: Number(form.store_id),
    customer_group_id: form.customer_group_id ? Number(form.customer_group_id) : null,
    name: form.name.trim(),
    email: form.email.trim(),
    telephone: form.telephone.trim(),
    alt_mobile: form.alt_mobile.trim(),
    language_id: Number(form.language_id),
    newsletter: Number(form.newsletter),
    customer_is_tax_free: Number(form.customer_is_tax_free),
    discount_percentage: form.discount_percentage?.toString() ?? "",
    status: Number(form.status),
    safe: Number(form.safe),
    call_customer: form.call_customer === "yes" ? "yes" : "no",
    vip: form.vip === "yes" ? "yes" : "no",
    default_payment_method: form.default_payment_method,
    ...(isEdit ? {} : { password: form.password }),
  };

  const requiredOk =
    payload.customer_group_id &&
    payload.name &&
    payload.telephone &&
    payload.language_id &&
    (isEdit || !!form.password);

  const formatError = (e) => {
    const data = e?.response?.data;
    if (data?.errors) {
      const firstKey = Object.keys(data.errors)[0];
      const firstErr = data.errors[firstKey];
      return Array.isArray(firstErr) ? firstErr[0] : firstErr;
    }
    return data?.message || "Save failed";
  };

  const handleSave = () => {
    if (!dirty || !requiredOk || saving) return;

    if (isEdit) {
      updateMut.mutate(
        { id: customer.customer_id, payload },
        {
          onSuccess: () => {
            setMsg("Saved");
            setDirty(false);
            qc.invalidateQueries({ queryKey: ["customers", "detail", customer.customer_id] });
            qc.invalidateQueries({ queryKey: ["customers", "list"] });
          },
          onError: (e) => setMsg(formatError(e)),
        }
      );
    } else {
      createMut.mutate(payload, {
        onSuccess: (res) => {
          setDirty(false);
          const newId = res?.data?.customer_id;
          qc.invalidateQueries({ queryKey: ["customers", "list"] });
          if (newId) {
            qc.invalidateQueries({ queryKey: ["customers", "detail", newId] });
            navigate(`/customers/${newId}`);
          }
          setMsg("Created");
        },
        onError: (e) => setMsg(formatError(e)),
      });
    }
  };

  return (
    <>
      <div className="title" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>{isEdit ? `Edit Customer #${customer.customer_id}` : "Create Customer"}</span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {msg && <span>{msg}</span>}
          <button
            type="button"
            className="oc-btn primary"
            onClick={handleSave}
            disabled={!dirty || !requiredOk || saving}
          >
            {saving ? "Saving…" : isEdit ? "Save" : "Create"}
          </button>
        </div>
      </div>

      <div className="form-group" style={{ display: "none" }}>
        <label className="col-sm-2 control-label" htmlFor="input-store-id">Store</label>
        <div className="col-sm-10">
          <select
            name="store_id"
            id="input-store-id"
            className="form-control"
            value={form.store_id}
            onChange={(e) => onChange("store_id", e.target.value)}
          >
            <option value="3">Abdallah Ghosheh</option>
          </select>
        </div>
      </div>

      <div className="oc-grid 2cols">
        <div className="oc-field">
          <label>Customer Group</label>
          {groupsLoading ? (
            <input className="form-control" value="Loading…" readOnly />
          ) : (
            <select
              name="customer_group_id"
              id="input-customer-group"
              className="form-control"
              value={form.customer_group_id}
              onChange={(e) => onChange("customer_group_id", e.target.value)}
            >
              <option value="" disabled>Select…</option>
              {groups.map(g => {
                const id = String(g.id ?? g.customer_group_id);
                const name = g.name ?? g.customer_group;
                return <option key={id} value={id}>{name}</option>;
              })}
            </select>
          )}
        </div>

        <div />

        <div className="oc-field">
          <label>Customer Name</label>
          <input
            type="text"
            name="name"
            id="input-name"
            className="form-control"
            value={form.name}
            onChange={(e) => onChange("name", e.target.value)}
          />
        </div>

        <div className="oc-field">
          <label>E-Mail</label>
          <input
            type="text"
            name="email"
            id="input-email"
            className="form-control"
            value={form.email}
            onChange={(e) => onChange("email", e.target.value)}
          />
        </div>

        <div className="oc-field">
          <label>Mobile</label>
          <input
            type="text"
            name="telephone"
            id="input-telephone"
            className="form-control"
            value={form.telephone}
            onChange={(e) => onChange("telephone", e.target.value)}
          />
        </div>

        <div className="oc-field">
          <label>Alternative Mobile</label>
          <input
            type="text"
            name="alt_mobile"
            id="input-alt-mobile"
            className="form-control"
            value={form.alt_mobile}
            onChange={(e) => onChange("alt_mobile", e.target.value)}
          />
        </div>
      </div>

      {!isEdit && (
        <fieldset>
          <legend>Password</legend>
          <div className="oc-grid 2cols">
            <div className="oc-field">
              <label>Password</label>
              <input
                type="password"
                name="password"
                id="input-password"
                className="form-control"
                autoComplete="off"
                value={form.password}
                onChange={(e) => onChange("password", e.target.value)}
              />
            </div>
          </div>
        </fieldset>
      )}

      <fieldset>
        <legend>Other</legend>

        <div className="oc-field">
          <label>Language</label>
          <select
            name="language_id"
            id="input-language_id"
            className="form-control"
            value={form.language_id}
            onChange={(e) => onChange("language_id", e.target.value)}
          >
            {LANG_OPTS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>

        <div className="oc-field">
          <label>Newsletter</label>
          <select
            name="newsletter"
            id="input-newsletter"
            className="form-control"
            value={form.newsletter}
            onChange={(e) => onChange("newsletter", e.target.value)}
          >
            {ENABLED_DISABLED.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>

        <div className="oc-field">
          <label>Is Tax Free</label>
          <select
            name="customer_is_tax_free"
            id="input-customer_is_tax_free"
            className="form-control"
            value={form.customer_is_tax_free}
            onChange={(e) => onChange("customer_is_tax_free", e.target.value)}
          >
            {YESNO_NUM.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>

        <div className="oc-field">
          <label>Default Coupon <span style={{ fontWeight: 400, color: "#888", fontSize: 12 }}>(optional)</span></label>
          <input
            type="text"
            name="discount_percentage"
            id="input-discount-percentage"
            className="form-control"
            placeholder="e.g. PROMO20"
            value={form.discount_percentage}
            onChange={(e) => onChange("discount_percentage", e.target.value)}
          />
        </div>

        <div className="oc-field">
          <label>Status</label>
          <select
            name="status"
            id="input-status"
            className="form-control"
            value={form.status}
            onChange={(e) => onChange("status", e.target.value)}
          >
            {ENABLED_DISABLED.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>

        <div className="oc-field">
          <label>Safe</label>
          <select
            name="safe"
            id="input-safe"
            className="form-control"
            value={form.safe}
            onChange={(e) => onChange("safe", e.target.value)}
          >
            {YESNO_NUM.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>

        <div className="oc-field">
          <label>Call before delivery</label>
          <select
            name="call_customer"
            id="input-call-customer"
            className="form-control"
            value={form.call_customer}
            onChange={(e) => onChange("call_customer", e.target.value)}
          >
            {YESNO_ENUM.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>

        <div className="oc-field">
          <label>VIP</label>
          <div className="checkbox">
            <label>
              <input
                type="checkbox"
                name="vip"
                value="yes"
                checked={form.vip === "yes"}
                onChange={(e) => onChange("vip", e.target.checked ? "yes" : "no")}
              />
              <span style={{ marginLeft: 6 }}>yes</span>
            </label>
          </div>
        </div>

        <div className="oc-field">
          <label>Default Payment Method</label>
          <select
            id="default_payment_method_select"
            name="default_payment_method"
            className="form-control"
            value={form.default_payment_method}
            onChange={(e) => onChange("default_payment_method", e.target.value)}
          >
            {PAY_METHODS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
          </select>
        </div>
      </fieldset>
    </>
  );
}

